import { Trans } from "@lingui/react";
import { ElementType } from "@mobility-item-page/utilities/client";
import { Box } from "@warp-ds/react/components/box";
import { ObjectPosition } from "../../types/objectPosition.ts";
import { DealerRatings } from "../components/DealerRatings.js";
import { ErrorInfo } from "../components/ErrorInfo.tsx";
import {
  AddressLink,
  DealerBrandingPageLink,
  GoogleMapsLink,
  OrgLookupLink,
  OtherAdsLink,
} from "../components/Links.tsx";
import { Logo } from "../components/Logo.tsx";
import { NotFound } from "../components/NotFound.tsx";
import { SafetyElementHighlight } from "../components/SafetyElementHighlight.tsx";
import { SalesPersonDetails } from "../components/SalesPersonDetails.tsx";
import { useValidDealerItemContext } from "../context/DealerItemProviderContext.tsx";
import { useTracking } from "../utils/pulseHelper.ts";

export const DealerInfoCard = () => {
  const {
    organisationData: { name, dealerDurationText, location },
  } = useValidDealerItemContext();
  const { trackCompanyRegistration, trackSeeOtherAdsClick, trackSeeLocation, trackDealerRatings } = useTracking();

  return (
    <Box bordered className="my-16 p-0!">
      <div className="p-16">
        <h2 className="sr-only">
          <Trans
            id="dealer.dealerInfoCard.srOnlyHeading"
            message="Dealer info card"
            comment="Screen-reader-only heading for the dealer info card"
          />
        </h2>
        <Logo className="mb-16" />
        <h3 className="mb-4">{name}</h3>
        <DealerRatings trackClick={(url) => trackDealerRatings(url, ObjectPosition.InfoCard)} />
        <OrgLookupLink trackClick={() => trackCompanyRegistration(ObjectPosition.InfoCard)} />
        {dealerDurationText && <p className="s-text-subtle mt-4">{dealerDurationText}</p>}
        <div className="flex flex-col space-y-12 mt-16">
          {location &&
            (location.googleMapInfo ? (
              <GoogleMapsLink
                trackClick={(url: string) => trackSeeLocation(ObjectPosition.InfoCard, ElementType.Link, url)}
                mapInfo={location.googleMapInfo}
              />
            ) : (
              location.mapInfo && (
                <AddressLink
                  mapInfo={location.mapInfo}
                  trackClick={() =>
                    trackSeeLocation(ObjectPosition.InfoCard, ElementType.Link, location.mapInfo?.linkUrl)
                  }
                />
              )
            ))}
          <DealerBrandingPageLink />
          <OtherAdsLink trackClick={() => trackSeeOtherAdsClick(ObjectPosition.InfoCard)} />
        </div>
      </div>
      <SalesPersonDetails />
      <SafetyElementHighlight />
    </Box>
  );
};

export const DealerInfoFallback = () => {
  return (
    <div className="border-2 s-border rounded-8 my-16 p-16 text-center">
      <h2 className="sr-only">
        <Trans
          id="dealer.dealerInfoCard.srOnlyHeading"
          message="Dealer info card"
          comment="Screen-reader-only heading for the dealer info card"
        />
      </h2>
      <NotFound className="mx-auto mb-16" />
      <ErrorInfo />
    </div>
  );
};
